import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Map as MapIcon, MapPin, MapPinOff, Search, X, Crosshair, ExternalLink } from 'lucide-react';
import { getProjects, getUsers, updateProject, getProjectBoundaries, getAllFellingAreas } from '../../services/api';
import { useMapLayers } from '../../hooks/useMapLayers';
import { LayerControlPanel } from '../../components/map/WmsLayers';
import { MapLayerSettingsDialog } from '../../components/map/MapLayerSettingsDialog';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { PROJECT_STATUS_ORDER, projectStatus, projectStatusLabel } from '../../lib/workStatus';
import { ProjectsLeafletMap, STATUS_PIN_COLORS } from '../../components/ProjectsLeafletMap';

const ALL_STATUSES = [...PROJECT_STATUS_ORDER, 'on_hold'];
const normalize = (status) => (status === 'active' ? 'in_progress' : status);

const ProjectsMap = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clientNames, setClientNames] = useState({});
  const [boundaries, setBoundaries] = useState([]);
  const [fellingAreas, setFellingAreas] = useState([]);
  const [layerSettingsOpen, setLayerSettingsOpen] = useState(false);
  const mapLayers = useMapLayers(true);
  const [loading, setLoading] = useState(true);
  const [activeStatuses, setActiveStatuses] = useState(new Set(ALL_STATUSES));
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [placing, setPlacing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [projectData, users, boundaryData, areaData] = await Promise.all([getProjects(), getUsers(), getProjectBoundaries().catch(() => []), getAllFellingAreas().catch(() => [])]);
      setProjects(projectData || []);
      setClientNames(Object.fromEntries((users || []).map((u) => [u.id, u.client_name])));
      setBoundaries(boundaryData || []);
      setFellingAreas(areaData || []);
    } catch (error) {
      toast.error('Neizdevās ielādēt objektus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => projects.reduce((acc, p) => { const s = normalize(p.status); acc[s] = (acc[s] || 0) + 1; return acc; }, {}), [projects]);

  const filtered = useMemo(() => projects.filter((p) => {
    if (!activeStatuses.has(normalize(p.status))) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [p.name, p.property_name, p.cadastral_number, p.property_address, clientNames[p.client_id]].some((v) => (v || '').toLowerCase().includes(q));
  }), [projects, activeStatuses, search, clientNames]);

  const located = filtered.filter((p) => p.latitude != null && p.longitude != null);
  const unlocated = filtered.filter((p) => p.latitude == null || p.longitude == null);

  const toggleStatus = (status) => setActiveStatuses((prev) => {
    const next = new Set(prev);
    if (next.has(status)) next.delete(status); else next.add(status);
    return next;
  });

  const saveCoordinates = async (project, latlng) => {
    try {
      const updated = await updateProject(project.id, { latitude: Number(latlng.lat.toFixed(6)), longitude: Number(latlng.lng.toFixed(6)) });
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, ...updated } : p)));
      toast.success(`Koordinātas saglabātas: ${project.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt koordinātas');
    }
  };

  const handlePlace = async (latlng) => {
    if (!placing) return;
    await saveCoordinates(placing, latlng);
    setSelectedId(placing.id);
    setPlacing(null);
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-[400px]"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-projects-map-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#2E2E2E] flex items-center gap-3" data-testid="admin-projects-map-title">
            <MapIcon className="w-8 h-8 text-[#1F3D2B]" /> Objektu karte
          </h1>
          <p className="text-[#2E2E2E]/60 mt-1">Visi objekti vienā skatā — adatiņas un īpašuma robežas krāsotas pēc statusa. Velciet adatiņu, lai precizētu atrašanās vietu; robežu failus (KML/GeoJSON) ielādē objekta detaļās.</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="map-status-filters">
          {ALL_STATUSES.map((status) => {
            const active = activeStatuses.has(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatus(status)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? 'bg-white border-[#1F3D2B]/30 text-[#2E2E2E] shadow-sm' : 'bg-gray-50 border-transparent text-[#2E2E2E]/40 line-through'}`}
                data-testid={`map-filter-${status}`}
                data-active={active}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_PIN_COLORS[status] }} />
                {projectStatusLabel(t, status)} <span className="text-[#2E2E2E]/50">{counts[status] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {placing && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 animate-fade-in" data-testid="map-placing-banner">
          <span className="flex items-center gap-2"><Crosshair className="w-4 h-4" /> Klikšķiniet kartē, lai novietotu objektu <strong>{placing.name}</strong></span>
          <Button size="sm" variant="ghost" onClick={() => setPlacing(null)} data-testid="map-placing-cancel"><X className="w-4 h-4 mr-1" /> Atcelt</Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        <Card className="border-0 shadow-sm lg:h-[calc(100vh-260px)] lg:min-h-[520px] flex flex-col overflow-hidden">
          <CardContent className="p-0 flex flex-col h-full">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Meklēt objektu, klientu, kadastru..." className="pl-9" data-testid="map-search" />
              </div>
              <p className="text-xs text-[#2E2E2E]/60 mt-2" data-testid="map-counts">Kartē: {located.length} · Bez koordinātām: {unlocated.length} · Ar robežām: {boundaries.filter((b) => filtered.some((p) => p.id === b.project_id)).length} · Cirsmas: {fellingAreas.filter((a) => filtered.some((p) => p.id === a.project_id)).length}</p>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100" data-testid="map-project-list">
              {located.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => setSelectedId(project.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-[#1F3D2B]/5 transition-colors ${selectedId === project.id ? 'bg-[#1F3D2B]/10' : ''}`}
                  data-testid={`map-list-item-${project.id}`}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: STATUS_PIN_COLORS[normalize(project.status)] }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#2E2E2E] truncate">{project.name}</p>
                      <p className="text-xs text-[#2E2E2E]/60 truncate">{clientNames[project.client_id] || '—'}{project.property_name ? ` · ${project.property_name}` : ''}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${projectStatus(project.status).className}`}>{projectStatusLabel(t, project.status)} · {project.progress}%</span>
                    </div>
                  </div>
                </button>
              ))}
              {unlocated.length > 0 && (
                <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-[#2E2E2E]/50 flex items-center gap-1.5"><MapPinOff className="w-3.5 h-3.5" /> Bez koordinātām</div>
              )}
              {unlocated.map((project) => (
                <div key={project.id} className="px-4 py-3 flex items-center justify-between gap-2" data-testid={`map-unlocated-${project.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2E2E2E] truncate">{project.name}</p>
                    <p className="text-xs text-[#2E2E2E]/60 truncate">{clientNames[project.client_id] || '—'} · {projectStatusLabel(t, project.status)}</p>
                  </div>
                  <Button size="sm" variant={placing?.id === project.id ? 'default' : 'outline'} className={placing?.id === project.id ? 'bg-[#1F3D2B]' : ''} onClick={() => setPlacing(placing?.id === project.id ? null : project)} data-testid={`map-place-${project.id}`}>
                    <Crosshair className="w-3.5 h-3.5 mr-1" /> Novietot
                  </Button>
                </div>
              ))}
              {filtered.length === 0 && <p className="px-4 py-10 text-center text-sm text-[#2E2E2E]/60" data-testid="map-list-empty">Nav objektu, kas atbilst filtriem</p>}
            </div>
            {selectedId && (
              <div className="p-3 border-t border-gray-100 bg-[#f8faf8]">
                <Button size="sm" className="w-full bg-[#1F3D2B] hover:bg-[#2a5039]" onClick={() => navigate(`/portal/admin/projects/${selectedId}`)} data-testid="map-open-selected">
                  <ExternalLink className="w-4 h-4 mr-2" /> Atvērt izvēlēto objektu
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm overflow-hidden relative h-[60vh] lg:h-[calc(100vh-260px)] lg:min-h-[520px]" data-testid="map-card">
          <LayerControlPanel mapLayers={mapLayers} canManage onManage={() => setLayerSettingsOpen(true)} />
          <MapLayerSettingsDialog open={layerSettingsOpen} onOpenChange={setLayerSettingsOpen} mapLayers={mapLayers} />
          <ProjectsLeafletMap
            projects={filtered}
            boundaries={boundaries}
            fellingAreas={fellingAreas}
            wmsLayers={mapLayers.activeLayers}
            clientNames={clientNames}
            selectedId={selectedId}
            onSelect={setSelectedId}
            placingActive={!!placing}
            onPlace={handlePlace}
            onMove={saveCoordinates}
            onOpenProject={(id) => navigate(`/portal/admin/projects/${id}`)}
            t={t}
          />
        </Card>
      </div>
    </div>
  );
};

export default ProjectsMap;
